<script lang="ts">
	import { Editor, type FocusPosition } from "@tiptap/core";
	import { Gapcursor } from "@tiptap/extension-gapcursor";
	import { History } from "@tiptap/extension-history";
	import { Paragraph } from "@tiptap/extension-paragraph";
	import { Placeholder } from "@tiptap/extension-placeholder";
	import { Text } from "@tiptap/extension-text";
	import { createEventDispatcher, onMount, type Snippet } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { DataType } from "#gen/common/v1/types_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import TokenMenu from "#lib/components/TokenMenu.svelte";
	import { DataToken } from "#lib/components/content-editors/extensions/DataToken.js";
	import { OneLiner } from "#lib/components/content-editors/extensions/OneLiner.js";
	import { dropdownRenderer } from "#lib/components/content-editors/extensions/dropdownRenderer.js";
	import { tokenizePlainText } from "#lib/components/content-editors/extensions/tokenParser.js";
	import type { SubstitutionToken, TokenProvider } from "#lib/tokenProvider.js";
	import Button from "#lib/ui/Button/index.js";
	import type { FormSizeType } from "flowbite-svelte";

	interface $$Events {
		change: CustomEvent<string | undefined>;
		focus: CustomEvent;
		blur: CustomEvent;
		paste: CustomEvent<string | undefined>;
		input: CustomEvent<string | undefined>;
	}

	interface Props {
		id?: string;
		provider: TokenProvider;
		value?: string;
		placeholder?: string;
		disabled?: boolean;
		size?: FormSizeType;
		color?: "base" | "green" | "red";
		disableSpace?: boolean;
		customInputClass?: string;
		wrapText?: boolean;
		autofocus?: FocusPosition;
		tokenFilter?: TokenFilter;
		class?: string;
		classLeft?: string;
		left?: Snippet;
		[key: string]: unknown;
	}

	type TokenFilter = (dataType: DataType) => boolean;

	const dispatch = createEventDispatcher();
	const inputWrapperClass = "grow items-center relative flex rounded-lg rounded-r-none border border-r-0 rtl:text-right overflow-x-auto no-scrollbar";
	const disabledClass = "cursor-not-allowed opacity-50";
	const floatClass = "flex absolute inset-y-2 border-l items-center text-gray-500 dark:text-gray-400 z-10";
	const leftPadding = { sm: "left-7", md: "left-8", lg: "left-9" };
	const borderClasses = {
		base: "border-gray-300 group-has-[[contenteditable]:focus]:border-primary-500",
		green: "border-green-500",
		red: "border-red-500",
	};
	const ringClasses = {
		base: "has-[[contenteditable]:focus]:border-primary-500 has-[[contenteditable]:focus]:ring-primary-500 dark:has-[[contenteditable]:focus]:border-primary-500 dark:has-[[contenteditable]:focus]:ring-primary-500",
		green: "focus-within:ring-green-500 focus-within:border-green-500 dark:focus-within:border-green-500 dark:focus-within:ring-green-500",
		red: "focus-within:ring-red-500 focus-within:border-red-500 dark:focus-within:ring-red-500 dark:focus-within:border-red-500",
	};
	const colorClasses = {
		base: "bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400",
		green: "bg-green-50 text-green-900 placeholder-green-700 dark:text-green-400 dark:placeholder-green-500 dark:bg-gray-700",
		red: "bg-red-50 text-red-900 placeholder-red-700 dark:text-red-500 dark:placeholder-red-500 dark:bg-gray-700",
	};
	const textSizes = { sm: "sm:text-xs", md: "text-sm", lg: "sm:text-base" };
	const inputSizes = { sm: "h-[34px]", md: "h-[42px]", lg: "h-[50px]" };
	const inputPadding = { sm: "px-2 py-1.5", md: "px-2.5 py-2", lg: "px-3 py-2.5" };

	let { provider: initialProvider, value = $bindable(), placeholder = $bindable(), disabled = false, size: initialSize = "md", color = "base", disableSpace = false, customInputClass, wrapText = false, autofocus = false, tokenFilter = excludeArrayTokens, left, class: className, classLeft, ...rest }: Props = $props();

	const size = $derived(initialSize || "md");
	const provider = $derived(
		initialProvider.withTokensOfType(
			...Object.values(DataType)
				.filter(v => typeof v === "number")
				.filter(dt => tokenFilter(dt)),
		),
	);

	let element: HTMLDivElement | undefined = $state();
	const editorState: { editor: Editor | null } = $state({ editor: null });
	let tokenMenuOpen = $state(false);
	let changed = false;
	let touched = false;
	let applyingExternalSync = false;

	onMount(() => {
		editorState.editor = new Editor({
			element,
			autofocus,
			editable: !disabled,
			content: tokenizePlainText(provider, value),
			injectCSS: false,
			parseOptions: {
				preserveWhitespace: "full", // dont strip extra spaces
			},
			extensions: [
				OneLiner,
				Paragraph,
				Gapcursor,
				Text,
				History,
				DataToken.configure({
					provider,
					extraAllowCondition: () => touched,
					suggestion: {
						items: ({ query }) => provider.suggest(query).map(i => ({ ...i.value, disableSpace })),
						render: dropdownRenderer,
					},
				}),
				Placeholder.configure({
					placeholder,
				}),
			],
			onBlur: props => {
				if ((props.event.relatedTarget as HTMLElement)?.matches(".plaintext-child *")) return;
				if (changed && !tokenMenuOpen) {
					dispatch("change", props.editor.getText());
					changed = false;
				}
				dispatch("blur");
			},
			onTransaction: ({ editor: ed, transaction }) => {
				if (applyingExternalSync || !transaction.docChanged) return;
				const text = ed.getText();
				if (textEquals(text, value)) return;
				setValue(text, true);
			},
			onFocus: () => {
				dispatch("focus");
			},
			onPaste: props => {
				dispatch("paste", props.clipboardData?.getData("text/plain"));
			},
			editorProps: {
				attributes: {
					class: twMerge("prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl focus:outline-none", !wrapText && "!whitespace-pre"),
				},
			},
		});
		return () => {
			editorState.editor?.destroy();
		};
	});

	$effect(() => {
		if (editorState.editor && !textEquals(editorState.editor.getText(), value)) pushEditorContent(value, provider);
	});

	function textEquals(a?: string, b?: string) {
		return (a ?? "") === (b ?? "");
	}

	function inSync(v?: string) {
		return textEquals(value, v) && textEquals(editorState.editor?.getText(), v);
	}

	function pushEditorContent(v: string | undefined, p = provider) {
		applyingExternalSync = true;
		editorState.editor?.commands.setContent(tokenizePlainText(p, v));
		applyingExternalSync = false;
	}

	function excludeArrayTokens(dataType: DataType): boolean {
		return dataType !== DataType.ARRAY;
	}

	export function setValue(newValue: string | undefined, skipExternalSync?: boolean) {
		if (inSync(newValue)) return;
		touched = true;
		changed = true;
		value = newValue;
		if (!skipExternalSync) {
			pushEditorContent(newValue);
		}
		dispatch("input", newValue);
	}

	export function renderValue(newValue: string | undefined) {
		if (inSync(newValue)) return;
		pushEditorContent(newValue);
		value = newValue;
		dispatch("input", newValue);
	}

	function handleTokenSelect(token: SubstitutionToken) {
		const command = editorState.editor?.chain().focus().insertToken(token);
		if (disableSpace) {
			command?.run();
		} else {
			command?.insertContent(" ").run();
		}
		tokenMenuOpen = false;
	}

	function handleOpenTokenMenu(e: Event) {
		e.preventDefault();
		e.stopImmediatePropagation();
		tokenMenuOpen = !tokenMenuOpen;
	}

	function handleCloseTokenMenu() {
		if (changed && tokenMenuOpen) {
			dispatch("change", editorState.editor?.getText());
			changed = false;
		}
		tokenMenuOpen = false;
	}
</script>

<div {...rest} class={twMerge("suggestion-anchor group relative rounded-lg border border-transparent", ringClasses[color], className)}>
	<div class="relative flex">
		{#if left}
			<div class="{twMerge(floatClass, colorClasses[color], borderClasses[color], classLeft)} pointer-events-none inset-s-0 ps-2.5 pr-2">
				{@render left?.()}
			</div>
		{/if}
		<div class={twMerge(inputWrapperClass, inputSizes[size], colorClasses[color], borderClasses[color], textSizes[size], customInputClass, disabled && disabledClass)}>
			<div role="textbox" tabindex="-1" class={twMerge("right-0 left-0 cursor-text", inputPadding[size], left && leftPadding[size], wrapText ? "h-full w-full" : "absolute")} onkeydown={() => {}} onclick={() => (!editorState.editor?.isFocused ? editorState.editor?.commands.focus("end") : undefined)}>
				<div class="relative w-full items-center overflow-visible">
					<div bind:this={element}></div>
				</div>
			</div>
		</div>
		<div class={twMerge("flex flex-none items-center rounded-lg rounded-l-none border border-l-0", borderClasses[color], colorClasses[color], disabled && disabledClass)}>
			<Button data-action-name="data-property-selector" data-action-title="Show data property selector" class={twMerge("mx-2 h-8 p-1", !disabled && "hover:bg-gray-200")} {disabled} color="none" on:mousedown={handleOpenTokenMenu}>
				<Icon iconSet="mdi" name="code-braces" class="h-4 w-4" />
				<span class="sr-only">Show token menu</span>
			</Button>
			<TokenMenu open={tokenMenuOpen} {provider} onselect={handleTokenSelect} onclose={handleCloseTokenMenu} />
		</div>
	</div>
</div>

<style>
	:global(img.ProseMirror-separator) {
		display: none;
	}

	:global(.ProseMirror p.is-editor-empty:first-child::before) {
		content: attr(data-placeholder);
		float: left;
		color: #9ca3af;
		pointer-events: none;
		height: 0;
	}
</style>
